import { lazy, Suspense, useEffect } from "react";
import { createPortal } from "react-dom";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "@src/store";
import { setConfetti } from "@src/store/slices/global";

const Confetti = lazy(async () => import("react-confetti"));

// Lucide "gem" icon (https://lucide.dev/icons/gem, viewBox 0 0 24 24). Each
// confetti particle is stroked as a gem instead of the library's default
// square/circle/strip. Paths are kept verbatim from the icon.
const GEM_PATH_DATA = [
  "M17 3a2 2 0 0 1 1.6.8l3 4a2 2 0 0 1 .013 2.382l-7.99 10.986a2 2 0 0 1-3.247 0l-7.99-10.986A2 2 0 0 1 2.4 7.8l2.998-3.997A2 2 0 0 1 7 3z",
  "M10.5 3 8 9l4 13 4-13-2.5-6",
  "M2 9h20",
];

const GEM_SIZE = 24;
const GEM_VIEWBOX = 32;

let gemPaths: Path2D[] | undefined;
const getGemPaths = () =>
  (gemPaths ??= GEM_PATH_DATA.map((d) => new Path2D(d)));

// react-confetti arrives with the canvas already translated/rotated to the
// particle and strokeStyle set to its colour, so we only scale the icon down,
// recentre its viewBox on the origin, and stroke.
const drawGem = (ctx: CanvasRenderingContext2D) => {
  const scale = GEM_SIZE / GEM_VIEWBOX;
  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(-GEM_VIEWBOX / 2, -GEM_VIEWBOX / 2);
  ctx.lineWidth = 2 / scale;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (const path of getGemPaths()) {
    ctx.stroke(path);
  }
  ctx.restore();
};

const VISIBLE_MS = 5000;
const FADE_MS = 2000;

// Mounted once, app-wide. Portalled to the body above the modal layer so the
// gems fall in front of celebration dialogs rather than behind their backdrop.
export const AppConfetti = () => {
  const confetti = useSelector((state) => state.global.confetti);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!confetti) {
      return;
    }

    const timeout = setTimeout(
      () => dispatch(setConfetti(false)),
      VISIBLE_MS + FADE_MS
    );

    return () => clearTimeout(timeout);
  }, [confetti, dispatch]);

  if (!confetti) {
    return null;
  }

  // The wrapper is a fresh node per celebration, so the animation restarts on
  // its own — holding fade state in React would leak across celebrations.
  return createPortal(
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: (theme) => theme.zIndex.modal + 1,
        "@keyframes igConfettiFadeOut": { to: { opacity: 0 } },
        animation: `igConfettiFadeOut ${FADE_MS}ms ease-out ${VISIBLE_MS}ms forwards`,
      }}
    >
      <Suspense fallback={null}>
        <Confetti
          gravity={0.1}
          recycle={false}
          colors={TimeSeriesColors}
          drawShape={drawGem}
        />
      </Suspense>
    </Box>,
    document.body
  );
};
